import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { LanguageSelector } from '@/components/LanguageSelector';
import { getErrorMessage } from '@/lib/error-utils';

export default function ResetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // 'checking' avoids both the error-flash while Supabase is still exchanging
  // the URL hash for a session, and the double-alert the no-token state used
  // to render (error banner + invalid-link banner saying the same thing twice).
  const [tokenState, setTokenState] = useState<'checking' | 'valid' | 'missing'>('checking');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation(['auth', 'validation']);

  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });

  // Dead link recovery: the primary action is requesting a new one right
  // here, not a login the invitee/expired-link holder cannot pass (P-10).
  const [requestEmail, setRequestEmail] = useState('');
  const [requestSending, setRequestSending] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  // Check for recovery session (Supabase processes hash automatically)
  useEffect(() => {
    const checkRecoverySession = async () => {
      try {
        // Supabase automatically exchanges hash parameters for a session
        // So we check for an active session instead of reading the hash
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('ResetPassword: Session error:', sessionError);
          setTokenState('missing');
          return;
        }

        if (session && session.user) {
          console.log('ResetPassword: Valid recovery session detected');
          setTokenState('valid');
        } else {
          console.log('ResetPassword: No recovery session found');
          setTokenState('missing');
        }
      } catch (err) {
        console.error('ResetPassword: Error checking session:', err);
        setTokenState('missing');
      }
    };

    checkRecoverySession();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validation
    if (passwordData.password !== passwordData.confirmPassword) {
      setError(t('validation:passwordMatch'));
      setIsLoading(false);
      return;
    }

    if (passwordData.password.length < 6) {
      setError(t('validation:passwordMin'));
      setIsLoading(false);
      return;
    }

    try {
      // Update password using the recovery token from the URL
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.password
      });

      if (updateError) {
        setError(updateError.message);
      } else {
        setSuccess(true);
        toast({
          title: t('auth:resetPasswordSuccessToastTitle'),
          description: t('auth:resetPasswordSuccessToastDescription'),
        });

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('auth:unexpectedErrorRetry'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestNewLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setRequestSending(true);
    setRequestError(null);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(requestEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (resetError) {
        setRequestError(resetError.message);
      } else {
        setRequestSent(true);
      }
    } catch (err: unknown) {
      setRequestError(getErrorMessage(err) || t('auth:unexpectedErrorRetry'));
    } finally {
      setRequestSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('auth:resetPasswordPageTitle')}</CardTitle>
          <CardDescription>
            {success
              ? t('auth:resetPasswordSuccessTitle')
              : tokenState === 'valid'
                ? t('auth:resetPasswordEnterNew')
                : t('auth:resetPasswordLinkProblemSubtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <Alert className="border-green-500 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                {t('auth:resetPasswordSuccessMessage')}
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {tokenState === 'checking' ? (
                <div className="flex items-center justify-center py-8" role="status">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : tokenState === 'valid' ? (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth:password')}</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder={t('auth:newPasswordPlaceholder')}
                      value={passwordData.password}
                      onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">{t('auth:confirmPassword')}</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder={t('auth:confirmNewPasswordPlaceholder')}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      disabled={isLoading}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('auth:resetPasswordSubmit')}
                  </Button>
                </form>
              ) : requestSent ? (
                <Alert className="border-green-500 bg-green-50 dark:border-green-700 dark:bg-green-950">
                  <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-green-800 dark:text-green-200">
                    {t('auth:resetPasswordNewLinkSent')}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  {requestError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{requestError}</AlertDescription>
                    </Alert>
                  )}
                  <form onSubmit={handleRequestNewLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="request-new-link-email">{t('auth:email')}</Label>
                      <Input
                        id="request-new-link-email"
                        type="email"
                        placeholder={t('auth:emailPlaceholder')}
                        value={requestEmail}
                        onChange={(e) => setRequestEmail(e.target.value)}
                        required
                        disabled={requestSending}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={requestSending || !requestEmail}>
                      {requestSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {t('auth:sendNewResetLink')}
                    </Button>
                  </form>
                  <Button
                    onClick={() => navigate('/auth')}
                    variant="link"
                    className="w-full text-xs underline"
                  >
                    {t('auth:backToLogin')}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
